/**
 * Admin Theme Creator — colours, decorations, drafts, publish, schedule, preview.
 */
import {
  deleteAdminTheme,
  deleteAdminThemeDraft,
  fetchAdminTheme,
  fetchAdminThemeDraft,
  fetchAdminThemeDrafts,
  fetchAdminThemes,
  patchAdminTheme,
  saveAdminTheme,
  saveAdminThemeDraft,
} from "../lib/cloud";
import {
  setThemePreviewData,
  refreshGameThemePresentation,
  loadPublishedGameTheme,
  setThemeDecorEditMode,
  getSelectedThemeDecorId,
  isThemePreviewActive,
  patchThemePreviewMusic,
} from "./game-theme";
import { openThemeDrawOverlay } from "./theme-draw-overlay";
import { mountThemeMusicEditor, type ThemeMusicEditorApi } from "./theme-music-editor";
import {
  emptyThemeData,
  normalizeThemeData,
  normalizeParticleConfig,
  detectCurrentThemeScreen,
  resolveThemeAttachTarget,
  themeDecorSizePctForBox,
  themeDecorSizePctForViewport,
  DEFAULT_PARTICLE_CONFIG,
  DEFAULT_THEME_MUSIC,
  THEME_PARTICLE_PRESETS,
  type ThemeData,
  type ThemeDecoration,
  type ThemeScreenTarget,
  type ThemeParticleConfig,
  type ThemeParticlePresetId,
  type ThemeMusicData,
} from "./theme-types";

export type AdminThemeCreatorApi = {
  refresh: () => void;
  getData: () => ThemeData;
  setData: (data: ThemeData) => void;
};

export type ThemePreviewHooks = {
  /** Leave Admin Panel and enter navigable preview with the current theme applied. */
  enterPreview: (api: {
    getData: () => ThemeData;
    setData: (data: ThemeData) => void;
    onExit: () => void;
  }) => void;
};

function uid(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(v: string): string | null {
  if (!v.trim()) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function openParticleSetupModal(initial?: {
  screens?: ThemeScreenTarget[];
  config?: ThemeParticleConfig;
  /** Primary confirm label — e.g. "Save Changes" when editing. */
  confirmLabel?: string;
  /** Optional second action (e.g. "Edit art") that also returns the form values. */
  artLabel?: string;
}): Promise<{
  screens: ThemeScreenTarget[];
  config: ThemeParticleConfig;
  editArt: boolean;
} | null> {
  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.className = "themeDrawImportModal";
    const box = document.createElement("div");
    box.className = "modeBox themeDrawImportBox themeParticleSetupBox";
    const h = document.createElement("h4");
    h.textContent = "Particle System";
    box.appendChild(h);

    const body = document.createElement("div");
    body.className = "themeParticleSetupBody";
    box.appendChild(body);

    const locHint = document.createElement("p");
    locHint.className = "accessHint";
    locHint.textContent = "Locations (combinable):";
    body.appendChild(locHint);

    const locKeys: ThemeScreenTarget[] = [
      "background",
      "sidebar",
      "home",
      "settings",
      "shop",
      "challenges",
    ];
    const initialScreens = new Set(initial?.screens || ["background"]);
    const checks = new Map<string, HTMLInputElement>();
    for (const key of locKeys) {
      const lab = document.createElement("label");
      lab.className = "adminThemeCheckLabel";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = initialScreens.has(key);
      checks.set(key, cb);
      lab.appendChild(cb);
      lab.appendChild(
        document.createTextNode(
          ` ${key === "background" ? "Background" : key[0]!.toUpperCase() + key.slice(1)}`,
        ),
      );
      body.appendChild(lab);
    }

    const presetHint = document.createElement("p");
    presetHint.className = "accessHint";
    presetHint.textContent = "Preset (editable below):";
    body.appendChild(presetHint);

    let config: ThemeParticleConfig = normalizeParticleConfig(
      initial?.config || DEFAULT_PARTICLE_CONFIG,
    );
    const presetRow = document.createElement("div");
    presetRow.className = "themeDrawActions";
    for (const id of Object.keys(THEME_PARTICLE_PRESETS) as ThemeParticlePresetId[]) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "btn";
      b.textContent = THEME_PARTICLE_PRESETS[id].label;
      b.onclick = () => {
        config = { ...THEME_PARTICLE_PRESETS[id].config };
        syncFields();
        for (const btn of presetRow.querySelectorAll("button")) btn.classList.remove("green");
        b.classList.add("green");
      };
      presetRow.appendChild(b);
    }
    body.appendChild(presetRow);

    const fields = document.createElement("div");
    fields.className = "themeParticleFields";
    body.appendChild(fields);

    type Field = {
      key: keyof ThemeParticleConfig;
      label: string;
      min: number;
      max: number;
      step: number;
    };
    const fieldDefs: Field[] = [
      { key: "spawnRate", label: "Spawn Rate (/s)", min: 1, max: 120, step: 1 },
      { key: "fallSpeedMin", label: "Fall Speed min", min: 5, max: 800, step: 1 },
      { key: "fallSpeedMax", label: "Fall Speed max", min: 5, max: 800, step: 1 },
      { key: "driftAmplitude", label: "Drift amplitude", min: 0, max: 120, step: 1 },
      { key: "driftFrequency", label: "Drift frequency", min: 0, max: 2, step: 0.05 },
      { key: "rotationSpeedMin", label: "Rotation min (°/s)", min: -360, max: 360, step: 1 },
      { key: "rotationSpeedMax", label: "Rotation max (°/s)", min: -360, max: 360, step: 1 },
      { key: "scaleMin", label: "Scale min", min: 0.1, max: 3, step: 0.05 },
      { key: "scaleMax", label: "Scale max", min: 0.1, max: 3, step: 0.05 },
      { key: "windAngleDeg", label: "Wind angle (°)", min: -180, max: 180, step: 1 },
      { key: "maxParticles", label: "Max particles", min: 10, max: 300, step: 1 },
      { key: "baseSizePx", label: "Base size (px)", min: 4, max: 64, step: 1 },
    ];
    const inputs = new Map<keyof ThemeParticleConfig, HTMLInputElement>();

    const fadeLab = document.createElement("label");
    fadeLab.className = "adminThemeCheckLabel";
    const fadeCb = document.createElement("input");
    fadeCb.type = "checkbox";
    fadeCb.checked = config.opacityFade;
    fadeCb.onchange = () => {
      config.opacityFade = fadeCb.checked;
    };
    fadeLab.appendChild(fadeCb);
    fadeLab.appendChild(document.createTextNode(" Opacity fade (spawn / exit)"));

    function syncFields() {
      for (const def of fieldDefs) {
        const input = inputs.get(def.key);
        if (input) input.value = String(config[def.key]);
      }
      fadeCb.checked = config.opacityFade;
    }

    for (const def of fieldDefs) {
      const row = document.createElement("label");
      row.className = "themeParticleFieldRow";
      const lab = document.createElement("span");
      lab.textContent = def.label;
      const input = document.createElement("input");
      input.type = "number";
      input.className = "formInput";
      input.min = String(def.min);
      input.max = String(def.max);
      input.step = String(def.step);
      input.value = String(config[def.key]);
      input.oninput = () => {
        const v = Number(input.value);
        if (!Number.isFinite(v)) return;
        switch (def.key) {
          case "spawnRate":
          case "fallSpeedMin":
          case "fallSpeedMax":
          case "driftAmplitude":
          case "driftFrequency":
          case "rotationSpeedMin":
          case "rotationSpeedMax":
          case "scaleMin":
          case "scaleMax":
          case "windAngleDeg":
          case "maxParticles":
          case "baseSizePx":
            config[def.key] = v;
            break;
          default:
            break;
        }
      };
      inputs.set(def.key, input);
      row.appendChild(lab);
      row.appendChild(input);
      fields.appendChild(row);
    }
    body.appendChild(fadeLab);

    function finish(editArt: boolean) {
      const screens = locKeys.filter((k) => checks.get(k)?.checked);
      if (!screens.length) {
        locHint.textContent = "Pick at least one location.";
        return;
      }
      modal.remove();
      resolve({ screens, config: normalizeParticleConfig(config), editArt });
    }

    const row = document.createElement("div");
    row.className = "themeDrawActions themeParticleSetupActions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn";
    cancel.textContent = "Cancel";
    cancel.onclick = () => {
      modal.remove();
      resolve(null);
    };
    row.appendChild(cancel);

    if (initial?.artLabel) {
      const art = document.createElement("button");
      art.type = "button";
      art.className = "btn";
      art.textContent = initial.artLabel;
      art.onclick = () => finish(true);
      row.appendChild(art);
    }

    const ok = document.createElement("button");
    ok.type = "button";
    ok.className = "btn green";
    ok.textContent = initial?.confirmLabel || "Draw particle";
    ok.onclick = () => finish(!!initial?.artLabel ? false : true);
    row.appendChild(ok);
    box.appendChild(row);
    modal.appendChild(box);
    document.body.appendChild(modal);
  });
}

function particlePlacementFromScreens(screens: ThemeScreenTarget[]): {
  anchor: "viewport" | "sidebar";
  layer: "front" | "back";
} {
  const hasBg = screens.includes("background");
  const onlySidebar = screens.includes("sidebar") && screens.every((s) => s === "sidebar");
  return {
    anchor: onlySidebar ? "sidebar" : "viewport",
    layer: hasBg || !onlySidebar ? "back" : "front",
  };
}

function openEditThemeDecoration(
  dec: ThemeDecoration,
  colors: { backgroundColor: string },
  onReplace: (next: ThemeDecoration) => void,
): void {
  if (dec.kind === "particles") {
    void (async () => {
      const setup = await openParticleSetupModal({
        screens: [...dec.screens],
        config: normalizeParticleConfig(dec.particleConfig),
        confirmLabel: "Save Changes",
        artLabel: "Edit art",
      });
      if (!setup) return;
      const place = particlePlacementFromScreens(setup.screens);
      const applyParams = (art?: { imageData: string; opacity: number }) => {
        onReplace({
          ...dec,
          kind: "particles",
          screens: setup.screens,
          particleConfig: setup.config,
          anchor: place.anchor,
          layer: place.layer,
          xPct: 0,
          yPct: 0,
          widthPct: 100,
          heightPct: 100,
          ...(art
            ? {
                imageData: art.imageData,
                frames: [art.imageData],
                opacity: art.opacity,
              }
            : {}),
        });
      };
      if (!setup.editArt) {
        applyParams();
        return;
      }
      const seed = dec.frames?.[0] || dec.imageData;
      openThemeDrawOverlay({
        mode: "static",
        title: "Edit particle art",
        surface: "canvas",
        backgroundColor: colors.backgroundColor,
        initialFrames: seed ? [seed] : undefined,
        initialOpacity: dec.opacity ?? 1,
        onCancel: () => {},
        onConfirm: (result) => {
          applyParams({ imageData: result.imageData, opacity: result.opacity });
        },
      });
    })();
    return;
  }
  if (dec.kind === "animated") {
    const loc =
      dec.screens.includes("sidebar") && !dec.screens.includes("background")
        ? "sidebar"
        : "background";
    openThemeDrawOverlay({
      mode: "animated",
      title: "Edit animation",
      surface: loc,
      backgroundColor: colors.backgroundColor,
      initialFrames: dec.frames?.length ? dec.frames : [dec.imageData],
      initialOpacity: dec.opacity ?? 1,
      onCancel: () => {},
      onConfirm: (result) => {
        onReplace({
          ...dec,
          imageData: result.imageData,
          frames: result.frames,
          opacity: result.opacity,
        });
      },
    });
    return;
  }
  openThemeDrawOverlay({
    mode: "static",
    title: "Edit decoration",
    surface: "canvas",
    initialFrames: [dec.imageData],
    initialOpacity: dec.opacity ?? 1,
    onCancel: () => {},
    onConfirm: (result) => {
      onReplace({
        ...dec,
        imageData: result.imageData,
        opacity: result.opacity,
      });
    },
  });
}

export function mountAdminThemeCreator(
  root: HTMLElement,
  hooks: ThemePreviewHooks,
): AdminThemeCreatorApi {
  root.replaceChildren();

  let data = emptyThemeData();
  let draftId: string | null = null;
  let editingThemeId: string | null = null;
  let busy = false;

  const heading = document.createElement("h3");
  heading.textContent = "Theme Creator";
  root.appendChild(heading);

  const hint = document.createElement("p");
  hint.className = "accessHint";
  hint.textContent =
    "Edit global colours (or tick Don't Change to keep each player's Settings colour), preview the whole game, and add seasonal decorations. Save drafts, keep multiple themes, and activate or schedule one for all players.";
  root.appendChild(hint);

  const status = document.createElement("p");
  status.className = "accessHint";
  root.appendChild(status);

  /* ── Colour controls ───────────────────────────────────────────────── */
  const colorBox = document.createElement("div");
  colorBox.className = "modeBox adminThemeColorBox";

  const colorTitle = document.createElement("h4");
  colorTitle.className = "adminPlayerStatsSubheading";
  colorTitle.textContent = "Colours";
  colorBox.appendChild(colorTitle);

  function colorRow(
    label: string,
    get: () => string,
    set: (hex: string) => void,
    dontChange?: { get: () => boolean; set: (v: boolean) => void },
  ): HTMLElement {
    const row = document.createElement("div");
    row.className = "formRow adminThemeColorRow";
    const lab = document.createElement("span");
    lab.className = "formLabel";
    lab.textContent = label;
    const input = document.createElement("input");
    input.type = "color";
    input.value = get();
    input.oninput = () => {
      if (dontChange?.get()) return;
      set(input.value);
      pushLivePreview();
    };
    const syncColor = () => {
      input.value = get();
    };
    row.appendChild(lab);
    row.appendChild(input);

    if (dontChange) {
      const checkLab = document.createElement("label");
      checkLab.className = "adminThemeDontChange";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = dontChange.get();
      const syncDont = () => {
        const off = dontChange.get();
        cb.checked = off;
        input.disabled = off;
        row.classList.toggle("adminThemeColorRow--dontChange", off);
      };
      cb.onchange = () => {
        dontChange.set(cb.checked);
        syncDont();
        pushLivePreview();
        syncGradVisibility();
      };
      checkLab.appendChild(cb);
      checkLab.appendChild(document.createTextNode("Don't Change"));
      row.appendChild(checkLab);
      (row as HTMLElement & { _sync?: () => void })._sync = () => {
        syncColor();
        syncDont();
      };
      syncDont();
    } else {
      (row as HTMLElement & { _sync?: () => void })._sync = syncColor;
    }
    return row;
  }

  function toggleRow(
    label: string,
    get: () => boolean,
    set: (v: boolean) => void,
  ): HTMLElement {
    const row = document.createElement("div");
    row.className = "formRow adminThemeColorRow";
    const lab = document.createElement("span");
    lab.className = "formLabel";
    lab.textContent = label;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "toggleSwitch";
    const sync = () => {
      const on = get();
      btn.classList.toggle("toggleOn", on);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    };
    btn.onclick = () => {
      set(!get());
      sync();
      pushLivePreview();
      syncGradVisibility();
    };
    (row as HTMLElement & { _sync?: () => void })._sync = sync;
    sync();
    row.appendChild(lab);
    row.appendChild(btn);
    return row;
  }

  const bgRow = colorRow(
    "Background",
    () => data.colors.backgroundColor,
    (h) => {
      data.colors.backgroundColor = h;
    },
    {
      get: () => data.colors.dontChangeBackground,
      set: (v) => {
        data.colors.dontChangeBackground = v;
      },
    },
  );
  const ballsRow = colorRow(
    "Balls",
    () => data.colors.ballsColor,
    (h) => {
      data.colors.ballsColor = h;
    },
    {
      get: () => data.colors.dontChangeBalls,
      set: (v) => {
        data.colors.dontChangeBalls = v;
      },
    },
  );
  const btnRow = colorRow(
    "Buttons",
    () => data.colors.buttonsColor,
    (h) => {
      data.colors.buttonsColor = h;
    },
    {
      get: () => data.colors.dontChangeButtons,
      set: (v) => {
        data.colors.dontChangeButtons = v;
      },
    },
  );
  const textRow = colorRow(
    "Text",
    () => data.colors.textColor,
    (h) => {
      data.colors.textColor = h;
    },
    {
      get: () => data.colors.dontChangeText,
      set: (v) => {
        data.colors.dontChangeText = v;
      },
    },
  );
  const bgGradToggle = toggleRow(
    "Background gradient",
    () => data.colors.backgroundGradient,
    (v) => {
      data.colors.backgroundGradient = v;
    },
  );
  const bgGradTo = colorRow(
    "Background gradient end",
    () => data.colors.backgroundGradientTo,
    (h) => {
      data.colors.backgroundGradientTo = h;
    },
  );
  const ballsGradToggle = toggleRow(
    "Balls gradient",
    () => data.colors.ballsGradient,
    (v) => {
      data.colors.ballsGradient = v;
    },
  );
  const ballsGradTo = colorRow(
    "Balls gradient end",
    () => data.colors.ballsGradientTo,
    (h) => {
      data.colors.ballsGradientTo = h;
    },
  );
  const btnGradToggle = toggleRow(
    "Buttons gradient",
    () => data.colors.buttonsGradient,
    (v) => {
      data.colors.buttonsGradient = v;
    },
  );
  const btnGradTo = colorRow(
    "Buttons gradient end",
    () => data.colors.buttonsGradientTo,
    (h) => {
      data.colors.buttonsGradientTo = h;
    },
  );
  const textGradToggle = toggleRow(
    "Text gradient",
    () => data.colors.textGradient,
    (v) => {
      data.colors.textGradient = v;
    },
  );
  const textGradTo = colorRow(
    "Text gradient end",
    () => data.colors.textGradientTo,
    (h) => {
      data.colors.textGradientTo = h;
    },
  );

  colorBox.appendChild(bgRow);
  colorBox.appendChild(ballsRow);
  colorBox.appendChild(btnRow);
  colorBox.appendChild(textRow);
  colorBox.appendChild(bgGradToggle);
  colorBox.appendChild(bgGradTo);
  colorBox.appendChild(ballsGradToggle);
  colorBox.appendChild(ballsGradTo);
  colorBox.appendChild(btnGradToggle);
  colorBox.appendChild(btnGradTo);
  colorBox.appendChild(textGradToggle);
  colorBox.appendChild(textGradTo);
  root.appendChild(colorBox);

  function syncGradVisibility() {
    const skipBg = data.colors.dontChangeBackground;
    const skipBalls = data.colors.dontChangeBalls;
    const skipBtn = data.colors.dontChangeButtons;
    const skipText = data.colors.dontChangeText;
    bgGradToggle.classList.toggle("hidden", skipBg);
    bgGradTo.classList.toggle("hidden", skipBg || !data.colors.backgroundGradient);
    ballsGradToggle.classList.toggle("hidden", skipBalls);
    ballsGradTo.classList.toggle("hidden", skipBalls || !data.colors.ballsGradient);
    btnGradToggle.classList.toggle("hidden", skipBtn);
    btnGradTo.classList.toggle("hidden", skipBtn || !data.colors.buttonsGradient);
    textGradToggle.classList.toggle("hidden", skipText);
    textGradTo.classList.toggle("hidden", skipText || !data.colors.textGradient);
  }
  syncGradVisibility();

  function syncColorInputs() {
    for (const row of [
      bgRow,
      ballsRow,
      btnRow,
      textRow,
      bgGradTo,
      ballsGradTo,
      btnGradTo,
      textGradTo,
      bgGradToggle,
      ballsGradToggle,
      btnGradToggle,
      textGradToggle,
    ]) {
      (row as HTMLElement & { _sync?: () => void })._sync?.();
    }
    syncGradVisibility();
  }

  function pushLivePreview() {
    setThemePreviewData(data);
  }

  /* ── Decorations list ──────────────────────────────────────────────── */
  const decorBox = document.createElement("div");
  decorBox.className = "modeBox";
  const decorTitle = document.createElement("h4");
  decorTitle.className = "adminPlayerStatsSubheading";
  decorTitle.textContent = "Decorations";
  decorBox.appendChild(decorTitle);
  const decorList = document.createElement("div");
  decorList.className = "adminThemeDecorList";
  decorBox.appendChild(decorList);
  root.appendChild(decorBox);

  function replaceDecoration(id: string, next: ThemeDecoration) {
    data.decorations = data.decorations.map((d) => (d.id === id ? next : d));
    renderDecorList();
    pushLivePreview();
  }

  function editDecoration(dec: ThemeDecoration) {
    openEditThemeDecoration(dec, data.colors, (next) => replaceDecoration(dec.id, next));
  }

  function renderDecorList() {
    decorList.replaceChildren();
    if (!data.decorations.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint";
      empty.textContent = "None yet — use Preview → Add Here / Add Animation.";
      decorList.appendChild(empty);
      return;
    }
    for (const dec of data.decorations) {
      const row = document.createElement("div");
      row.className = "adminThemeDecorRow";
      const thumb = document.createElement("img");
      thumb.src = dec.imageData;
      thumb.alt = "";
      thumb.className = "adminThemeDecorThumb";
      const meta = document.createElement("div");
      meta.className = "accessHint";
      meta.textContent = `${dec.kind} · ${dec.screens.join("+")} · ${Math.round(dec.widthPct)}%×${Math.round(dec.heightPct)}%${
        dec.kind === "particles" ? ` · ${dec.particleConfig?.spawnRate ?? "?"}/s` : ""
      }`;
      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "btn";
      edit.textContent = "Edit";
      edit.onclick = () => editDecoration(dec);
      const del = document.createElement("button");
      del.type = "button";
      del.className = "btn";
      del.textContent = "Remove";
      del.onclick = () => {
        data.decorations = data.decorations.filter((d) => d.id !== dec.id);
        renderDecorList();
        pushLivePreview();
      };
      row.appendChild(thumb);
      row.appendChild(meta);
      row.appendChild(edit);
      row.appendChild(del);
      decorList.appendChild(row);
    }
  }

  /* ── Music composer ────────────────────────────────────────────────── */
  if (!data.music) data.music = { ...DEFAULT_THEME_MUSIC, cells: [] };
  let musicEditorApi: ThemeMusicEditorApi | null = mountThemeMusicEditor(root, {
    getMusic: () => data.music || { ...DEFAULT_THEME_MUSIC, cells: [] },
    setMusic: (music: ThemeMusicData) => {
      data.music = music;
      if (isThemePreviewActive()) patchThemePreviewMusic(music);
    },
  });

  /* ── Primary actions ───────────────────────────────────────────────── */
  const actions = document.createElement("div");
  actions.className = "adminSplashActions";

  const previewBtn = document.createElement("button");
  previewBtn.type = "button";
  previewBtn.className = "btn green";
  previewBtn.textContent = "Open Preview";
  previewBtn.onclick = () => {
    pushLivePreview();
    hooks.enterPreview({
      getData: () => data,
      setData: (next) => {
        data = normalizeThemeData(next);
        if (!data.music) data.music = { ...DEFAULT_THEME_MUSIC, cells: [] };
        syncColorInputs();
        renderDecorList();
        musicEditorApi?.setMusic(data.music);
        pushLivePreview();
      },
      onExit: () => {
        /* keep colours applied until cleared; panel remount refresh */
        status.textContent = "Returned from preview.";
        syncColorInputs();
        renderDecorList();
        musicEditorApi?.setMusic(data.music || { ...DEFAULT_THEME_MUSIC, cells: [] });
      },
    });
  };

  const saveDraftBtn = document.createElement("button");
  saveDraftBtn.type = "button";
  saveDraftBtn.className = "btn";
  saveDraftBtn.textContent = "Save Draft";

  const saveThemeBtn = document.createElement("button");
  saveThemeBtn.type = "button";
  saveThemeBtn.className = "btn green";
  saveThemeBtn.textContent = "Save Theme";

  const newBtn = document.createElement("button");
  newBtn.type = "button";
  newBtn.className = "btn";
  newBtn.textContent = "New";
  newBtn.onclick = () => {
    data = emptyThemeData();
    draftId = null;
    editingThemeId = null;
    nameInput.value = "";
    scheduleStart.value = "";
    scheduleEnd.value = "";
    syncColorInputs();
    renderDecorList();
    musicEditorApi?.setMusic(data.music || { ...DEFAULT_THEME_MUSIC, cells: [] });
    setThemePreviewData(null);
    status.textContent = "Started a new theme.";
  };

  actions.appendChild(previewBtn);
  actions.appendChild(saveDraftBtn);
  actions.appendChild(saveThemeBtn);
  actions.appendChild(newBtn);
  root.appendChild(actions);

  /* ── Name + schedule ───────────────────────────────────────────────── */
  const metaBox = document.createElement("div");
  metaBox.className = "modeBox";
  const nameRow = document.createElement("div");
  nameRow.className = "formRow";
  const nameLab = document.createElement("span");
  nameLab.className = "formLabel";
  nameLab.textContent = "Theme name";
  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.className = "formInput";
  nameInput.placeholder = "e.g. Christmas";
  nameInput.maxLength = 120;
  nameRow.appendChild(nameLab);
  nameRow.appendChild(nameInput);
  metaBox.appendChild(nameRow);

  const schedRow = document.createElement("div");
  schedRow.className = "formRow adminThemeSchedRow";
  const startLab = document.createElement("span");
  startLab.className = "formLabel";
  startLab.textContent = "Schedule start";
  const scheduleStart = document.createElement("input");
  scheduleStart.type = "datetime-local";
  scheduleStart.className = "formInput";
  const endLab = document.createElement("span");
  endLab.className = "formLabel";
  endLab.textContent = "Schedule end";
  const scheduleEnd = document.createElement("input");
  scheduleEnd.type = "datetime-local";
  scheduleEnd.className = "formInput";
  schedRow.appendChild(startLab);
  schedRow.appendChild(scheduleStart);
  schedRow.appendChild(endLab);
  schedRow.appendChild(scheduleEnd);
  metaBox.appendChild(schedRow);
  root.appendChild(metaBox);

  saveDraftBtn.onclick = async () => {
    if (busy) return;
    busy = true;
    status.textContent = "Saving draft…";
    try {
      const item = await saveAdminThemeDraft({
        id: draftId || undefined,
        name: nameInput.value.trim() || undefined,
        data,
      });
      draftId = item.id;
      status.textContent = `Draft saved (${item.name || item.id}).`;
      await refreshLists();
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Failed to save draft.";
    } finally {
      busy = false;
    }
  };

  saveThemeBtn.onclick = async () => {
    if (busy) return;
    const name = nameInput.value.trim();
    if (!name) {
      status.textContent = "Enter a theme name before saving.";
      return;
    }
    busy = true;
    status.textContent = "Saving theme…";
    try {
      const startIso = fromDatetimeLocalValue(scheduleStart.value);
      const endIso = fromDatetimeLocalValue(scheduleEnd.value);
      if (editingThemeId) {
        await patchAdminTheme({
          id: editingThemeId,
          action: "update",
          name,
          data,
          scheduleStart: startIso,
          scheduleEnd: endIso,
        });
        status.textContent = `Theme “${name}” updated.`;
      } else {
        const item = await saveAdminTheme({
          name,
          data,
          scheduleStart: startIso,
          scheduleEnd: endIso,
        });
        editingThemeId = item.id;
        status.textContent = `Theme “${item.name}” saved. Activate it when ready.`;
      }
      await refreshLists();
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Failed to save theme.";
    } finally {
      busy = false;
    }
  };

  /* ── Lists: drafts + themes ────────────────────────────────────────── */
  const listsWrap = document.createElement("div");
  listsWrap.className = "adminThemeLists";

  const draftsBox = document.createElement("div");
  draftsBox.className = "modeBox";
  const draftsH = document.createElement("h4");
  draftsH.className = "adminPlayerStatsSubheading";
  draftsH.textContent = "Drafts";
  draftsBox.appendChild(draftsH);
  const draftsList = document.createElement("div");
  draftsList.className = "leaderboardWrap";
  draftsBox.appendChild(draftsList);

  const themesBox = document.createElement("div");
  themesBox.className = "modeBox";
  const themesH = document.createElement("h4");
  themesH.className = "adminPlayerStatsSubheading";
  themesH.textContent = "Saved themes";
  themesBox.appendChild(themesH);

  const deactivateBtn = document.createElement("button");
  deactivateBtn.type = "button";
  deactivateBtn.className = "btn";
  deactivateBtn.textContent = "Deactivate active theme";
  deactivateBtn.onclick = async () => {
    if (busy) return;
    busy = true;
    try {
      await patchAdminTheme({ id: "_", action: "deactivate" });
      status.textContent = "No theme is active — players see default appearance.";
      await refreshLists();
      await loadPublishedGameTheme();
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Deactivate failed.";
    } finally {
      busy = false;
    }
  };
  themesBox.appendChild(deactivateBtn);

  const themesList = document.createElement("div");
  themesList.className = "leaderboardWrap";
  themesBox.appendChild(themesList);

  listsWrap.appendChild(draftsBox);
  listsWrap.appendChild(themesBox);
  root.appendChild(listsWrap);

  async function refreshLists() {
    try {
      const [drafts, themes] = await Promise.all([fetchAdminThemeDrafts(), fetchAdminThemes()]);
      draftsList.replaceChildren();
      if (!drafts.length) {
        const p = document.createElement("p");
        p.className = "accessHint";
        p.textContent = "No drafts.";
        draftsList.appendChild(p);
      } else {
        for (const d of drafts) {
          const row = document.createElement("div");
          row.className = "adminThemeListRow";
          const label = document.createElement("span");
          label.textContent = `${d.name || "(unnamed)"} · ${d.authorName || "?"} · ${new Date(d.updatedAt).toLocaleString()}`;
          const load = document.createElement("button");
          load.type = "button";
          load.className = "btn";
          load.textContent = "Load";
          load.onclick = async () => {
            try {
              const item = await fetchAdminThemeDraft(d.id);
              data = normalizeThemeData(item.data);
              draftId = item.id;
              editingThemeId = null;
              nameInput.value = item.name || "";
              syncColorInputs();
              renderDecorList();
              musicEditorApi?.setMusic(data.music || { ...DEFAULT_THEME_MUSIC, cells: [] });
              pushLivePreview();
              status.textContent = `Loaded draft ${item.name || item.id}.`;
            } catch (e) {
              status.textContent = e instanceof Error ? e.message : "Load failed.";
            }
          };
          const del = document.createElement("button");
          del.type = "button";
          del.className = "btn";
          del.textContent = "Delete";
          del.onclick = async () => {
            try {
              await deleteAdminThemeDraft(d.id);
              if (draftId === d.id) draftId = null;
              await refreshLists();
            } catch (e) {
              status.textContent = e instanceof Error ? e.message : "Delete failed.";
            }
          };
          row.appendChild(label);
          row.appendChild(load);
          row.appendChild(del);
          draftsList.appendChild(row);
        }
      }

      themesList.replaceChildren();
      if (!themes.items.length) {
        const p = document.createElement("p");
        p.className = "accessHint";
        p.textContent = "No saved themes.";
        themesList.appendChild(p);
      } else {
        for (const t of themes.items) {
          const row = document.createElement("div");
          row.className = "adminThemeListRow";
          const badge =
            themes.activeId === t.id || t.liveNow ? " [LIVE]" : t.isActive ? " [ACTIVE]" : "";
          const label = document.createElement("span");
          label.textContent = `${t.name}${badge} · ${t.authorName || "?"}`;
          const load = document.createElement("button");
          load.type = "button";
          load.className = "btn";
          load.textContent = "Edit";
          load.onclick = async () => {
            try {
              const item = await fetchAdminTheme(t.id);
              data = normalizeThemeData(item.data);
              editingThemeId = item.id;
              draftId = null;
              nameInput.value = item.name;
              scheduleStart.value = toDatetimeLocalValue(item.scheduleStart);
              scheduleEnd.value = toDatetimeLocalValue(item.scheduleEnd);
              syncColorInputs();
              renderDecorList();
              musicEditorApi?.setMusic(data.music || { ...DEFAULT_THEME_MUSIC, cells: [] });
              pushLivePreview();
              status.textContent = `Editing “${item.name}”.`;
            } catch (e) {
              status.textContent = e instanceof Error ? e.message : "Load failed.";
            }
          };
          const act = document.createElement("button");
          act.type = "button";
          act.className = "btn green";
          act.textContent = "Activate";
          act.onclick = async () => {
            try {
              await patchAdminTheme({ id: t.id, action: "activate" });
              status.textContent = `“${t.name}” is now active for players.`;
              await refreshLists();
              await loadPublishedGameTheme();
            } catch (e) {
              status.textContent = e instanceof Error ? e.message : "Activate failed.";
            }
          };
          const del = document.createElement("button");
          del.type = "button";
          del.className = "btn";
          del.textContent = "Delete";
          del.onclick = async () => {
            try {
              await deleteAdminTheme(t.id);
              if (editingThemeId === t.id) editingThemeId = null;
              await refreshLists();
            } catch (e) {
              status.textContent = e instanceof Error ? e.message : "Delete failed.";
            }
          };
          row.appendChild(label);
          row.appendChild(load);
          row.appendChild(act);
          row.appendChild(del);
          themesList.appendChild(row);
        }
      }
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Could not load theme lists.";
    }
  }

  renderDecorList();
  void refreshLists();

  return {
    refresh: () => {
      void refreshLists();
      syncColorInputs();
      renderDecorList();
      musicEditorApi?.setMusic(data.music || { ...DEFAULT_THEME_MUSIC, cells: [] });
    },
    getData: () => data,
    setData: (next) => {
      data = normalizeThemeData(next);
      if (!data.music) data.music = { ...DEFAULT_THEME_MUSIC, cells: [] };
      syncColorInputs();
      renderDecorList();
      musicEditorApi?.setMusic(data.music);
    },
  };
}

/** Floating chrome while navigating the game in theme preview mode. */
export function mountThemePreviewChrome(opts: {
  getData: () => ThemeData;
  setData: (data: ThemeData) => void;
  onBack: () => void;
}): { destroy: () => void } {
  const bar = document.createElement("div");
  bar.id = "themePreviewChrome";
  bar.className = "themePreviewChrome";

  const dragHandle = document.createElement("span");
  dragHandle.className = "themePreviewChromeLabel themePreviewChromeDrag";
  dragHandle.textContent = "Theme Preview";
  dragHandle.title = "Drag to move";
  bar.appendChild(dragHandle);

  /* Restore saved position */
  try {
    const raw = localStorage.getItem("themePreviewChromePos");
    if (raw) {
      const pos = JSON.parse(raw) as { left?: number; top?: number };
      if (typeof pos.left === "number" && typeof pos.top === "number") {
        bar.style.left = `${pos.left}px`;
        bar.style.top = `${pos.top}px`;
        bar.style.transform = "none";
      }
    }
  } catch {
    /* ignore */
  }

  let drag: { ox: number; oy: number; sl: number; st: number } | null = null;
  dragHandle.onpointerdown = (ev) => {
    ev.preventDefault();
    const rect = bar.getBoundingClientRect();
    drag = { ox: ev.clientX, oy: ev.clientY, sl: rect.left, st: rect.top };
    dragHandle.setPointerCapture(ev.pointerId);
  };
  dragHandle.onpointermove = (ev) => {
    if (!drag) return;
    const left = Math.max(0, Math.min(window.innerWidth - 80, drag.sl + (ev.clientX - drag.ox)));
    const top = Math.max(0, Math.min(window.innerHeight - 40, drag.st + (ev.clientY - drag.oy)));
    bar.style.left = `${left}px`;
    bar.style.top = `${top}px`;
    bar.style.transform = "none";
  };
  dragHandle.onpointerup = () => {
    if (!drag) return;
    drag = null;
    try {
      localStorage.setItem(
        "themePreviewChromePos",
        JSON.stringify({ left: parseFloat(bar.style.left) || 0, top: parseFloat(bar.style.top) || 0 }),
      );
    } catch {
      /* ignore */
    }
  };

  const addHere = document.createElement("button");
  addHere.type = "button";
  addHere.className = "btn green";
  addHere.textContent = "Add Here";
  bar.appendChild(addHere);

  const addAnim = document.createElement("button");
  addAnim.type = "button";
  addAnim.className = "btn";
  addAnim.textContent = "Add Animation";
  bar.appendChild(addAnim);

  let editing = false;
  const editBtn = document.createElement("button");
  editBtn.type = "button";
  editBtn.className = "btn";
  editBtn.textContent = "Edit art";
  const editSelectedBtn = document.createElement("button");
  editSelectedBtn.type = "button";
  editSelectedBtn.className = "btn";
  editSelectedBtn.textContent = "Edit selected";
  editSelectedBtn.classList.add("hidden");
  editSelectedBtn.onclick = () => {
    const id = getSelectedThemeDecorId();
    if (!id) return;
    const data = normalizeThemeData(opts.getData());
    const dec = data.decorations.find((d) => d.id === id);
    if (!dec) return;
    openEditThemeDecoration(dec, data.colors, (next) => {
      const cur = normalizeThemeData(opts.getData());
      cur.decorations = cur.decorations.map((d) => (d.id === id ? next : d));
      opts.setData(cur);
      setThemePreviewData(cur);
      refreshGameThemePresentation();
      try {
        sessionStorage.setItem("cgThemePreviewPayload", JSON.stringify(cur));
      } catch {
        /* ignore */
      }
    });
  };

  editBtn.onclick = () => {
    editing = !editing;
    editBtn.classList.toggle("green", editing);
    editBtn.textContent = editing ? "Done editing" : "Edit art";
    editSelectedBtn.classList.toggle("hidden", !editing);
    setThemeDecorEditMode(editing, (id, patch) => {
      const data = opts.getData();
      if (patch === "delete") {
        data.decorations = data.decorations.filter((d) => d.id !== id);
        opts.setData(normalizeThemeData(data));
      } else {
        // Mutate in place and avoid opts.setData → full remount (that snapped drag position).
        data.decorations = data.decorations.map((d) => (d.id === id ? { ...d, ...patch } : d));
        try {
          sessionStorage.setItem(THEME_PREVIEW_STORAGE_KEY, JSON.stringify(normalizeThemeData(data)));
        } catch {
          /* ignore */
        }
      }
    });
  };
  bar.appendChild(editBtn);
  bar.appendChild(editSelectedBtn);

  const sizeLabel = document.createElement("span");
  sizeLabel.className = "themePreviewChromeLabel";
  sizeLabel.textContent = "View";
  bar.appendChild(sizeLabel);

  type DevicePreview = "desktop" | "tablet" | "mobile";
  let devicePreview: DevicePreview = "desktop";
  let deviceFrame: HTMLElement | null = null;
  let deviceIframe: HTMLIFrameElement | null = null;

  const THEME_PREVIEW_STORAGE_KEY = "cgThemePreviewPayload";

  function writePreviewPayload() {
    try {
      sessionStorage.setItem(THEME_PREVIEW_STORAGE_KEY, JSON.stringify(opts.getData()));
    } catch {
      /* ignore */
    }
  }

  function closeDeviceFrame() {
    deviceFrame?.remove();
    deviceFrame = null;
    deviceIframe = null;
    document.body.classList.remove("theme-preview-device-open");
  }

  function openDeviceFrame(kind: "tablet" | "mobile") {
    closeDeviceFrame();
    writePreviewPayload();
    const frame = document.createElement("div");
    frame.className = "themePreviewDeviceFrame";
    frame.dataset.device = kind;
    const chrome = document.createElement("div");
    chrome.className = "themePreviewDeviceChrome";
    const title = document.createElement("span");
    title.textContent = kind === "tablet" ? "Tablet · 768×1024" : "Mobile · 390×844";
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "btn";
    closeBtn.textContent = "Close";
    closeBtn.onclick = () => {
      devicePreview = "desktop";
      syncDeviceBtns();
      closeDeviceFrame();
    };
    chrome.appendChild(title);
    chrome.appendChild(closeBtn);
    const stage = document.createElement("div");
    stage.className = "themePreviewDeviceStage";
    const iframe = document.createElement("iframe");
    iframe.className = "themePreviewDeviceIframe";
    iframe.title = `Theme preview (${kind})`;
    const url = new URL(window.location.href);
    url.searchParams.set("theme_device_preview", "1");
    iframe.src = url.toString();
    stage.appendChild(iframe);
    frame.appendChild(chrome);
    frame.appendChild(stage);
    document.body.appendChild(frame);
    document.body.classList.add("theme-preview-device-open");
    deviceFrame = frame;
    deviceIframe = iframe;
  }

  function syncDeviceBtns() {
    for (const [key, btn] of deviceBtns) {
      btn.classList.toggle("green", devicePreview === key);
    }
  }

  const deviceBtns = new Map<DevicePreview, HTMLButtonElement>();
  for (const key of ["desktop", "tablet", "mobile"] as const) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "btn";
    b.textContent = key === "desktop" ? "Desktop" : key === "tablet" ? "Tablet" : "Mobile";
    b.onclick = () => {
      devicePreview = key;
      syncDeviceBtns();
      if (key === "desktop") closeDeviceFrame();
      else openDeviceFrame(key);
    };
    deviceBtns.set(key, b);
    bar.appendChild(b);
  }
  syncDeviceBtns();

  const back = document.createElement("button");
  back.type = "button";
  back.className = "btn";
  back.textContent = "Back to Editor";
  back.onclick = () => opts.onBack();
  bar.appendChild(back);

  document.body.appendChild(bar);
  document.body.classList.add("theme-preview-mode");
  writePreviewPayload();

  function commitDecoration(dec: ThemeDecoration) {
    const data = normalizeThemeData(opts.getData());
    data.decorations = [...data.decorations, dec];
    opts.setData(data);
    setThemePreviewData(data);
    refreshGameThemePresentation();
    writePreviewPayload();
    if (deviceIframe?.contentWindow) {
      try {
        deviceIframe.contentWindow.location.reload();
      } catch {
        /* ignore */
      }
    }
  }

  function placeDecorationInteractive(
    kind: "static" | "animated",
    imageData: string,
    frames: string[] | undefined,
    screens: ThemeScreenTarget[],
    opacity: number,
  ) {
    const ghost = document.createElement("img");
    ghost.className = "themePlaceGhost";
    ghost.src = imageData;
    ghost.alt = "";
    ghost.draggable = false;
    ghost.style.opacity = String(opacity);
    document.body.appendChild(ghost);

    const hint = document.createElement("div");
    hint.className = "themePlaceHint";
    hint.textContent = "Click to plant — anchors to nearby UI when possible (Esc to cancel)";
    document.body.appendChild(hint);

    const onMove = (ev: PointerEvent) => {
      ghost.style.left = `${ev.clientX}px`;
      ghost.style.top = `${ev.clientY}px`;
    };

    const cleanup = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
      ghost.remove();
      hint.remove();
    };

    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") cleanup();
    };

    const onDown = (ev: PointerEvent) => {
      if ((ev.target as HTMLElement | null)?.closest?.("#themePreviewChrome")) return;
      if ((ev.target as HTMLElement | null)?.closest?.(".themePreviewDeviceFrame")) return;
      ev.preventDefault();
      ev.stopPropagation();

      const sideNav = document.getElementById("sideNav");
      const sideRect = sideNav?.getBoundingClientRect();
      const overSidebar =
        !!sideRect &&
        ev.clientX >= sideRect.left &&
        ev.clientX <= sideRect.right &&
        ev.clientY >= sideRect.top &&
        ev.clientY <= sideRect.bottom;

      const attach = !overSidebar ? resolveThemeAttachTarget(ev.clientX, ev.clientY) : null;

      if (attach) {
        const box = attach.el.getBoundingClientRect();
        if (box.width > 0 && box.height > 0) {
          const { widthPct, heightPct } = themeDecorSizePctForBox(box);
          const xPct = ((ev.clientX - box.left) / box.width) * 100 - widthPct / 2;
          const yPct = ((ev.clientY - box.top) / box.height) * 100 - heightPct / 2;
          commitDecoration({
            id: uid("dec"),
            kind,
            imageData,
            frames: kind === "animated" ? frames : undefined,
            anchor: "viewport",
            xPct,
            yPct,
            widthPct,
            heightPct,
            screens: screens.length ? screens : [detectCurrentThemeScreen()],
            zIndex: 5,
            opacity,
            layer: "front",
            attachSelector: attach.selector,
          });
          cleanup();
          return;
        }
      }

      const anchor: "viewport" | "sidebar" = overSidebar ? "sidebar" : "viewport";
      const box =
        anchor === "sidebar" && sideRect && sideRect.width > 0
          ? sideRect
          : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };

      const size =
        anchor === "sidebar"
          ? themeDecorSizePctForBox(box, 72)
          : themeDecorSizePctForViewport(box.width, box.height);
      const widthPct = anchor === "sidebar" ? Math.max(size.widthPct, 28) : size.widthPct;
      const heightPct = anchor === "sidebar" ? Math.max(size.heightPct, 8) : size.heightPct;
      const xPct = Math.max(
        -10,
        Math.min(100, ((ev.clientX - box.left) / box.width) * 100 - widthPct / 2),
      );
      const yPct = Math.max(
        -10,
        Math.min(100, ((ev.clientY - box.top) / box.height) * 100 - heightPct / 2),
      );

      commitDecoration({
        id: uid("dec"),
        kind,
        imageData,
        frames: kind === "animated" ? frames : undefined,
        anchor,
        xPct,
        yPct,
        widthPct,
        heightPct,
        screens: screens.length ? screens : [detectCurrentThemeScreen()],
        zIndex: 5,
        opacity,
        layer: "front",
      });
      cleanup();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
  }

  function pickAnimMode(): Promise<"frames" | "particles" | null> {
    return new Promise((resolve) => {
      const modal = document.createElement("div");
      modal.className = "themeDrawImportModal";
      const box = document.createElement("div");
      box.className = "modeBox themeDrawImportBox";
      const h = document.createElement("h4");
      h.textContent = "Add Animation";
      box.appendChild(h);
      const hint = document.createElement("p");
      hint.className = "accessHint";
      hint.textContent =
        "Frame Animation — short looping sprite. Particle System — continuous snow, rain, leaves, sparks (single particle sprite).";
      box.appendChild(hint);

      let chosen: "frames" | "particles" = "frames";
      for (const key of ["frames", "particles"] as const) {
        const lab = document.createElement("label");
        lab.className = "adminThemeCheckLabel";
        const rb = document.createElement("input");
        rb.type = "radio";
        rb.name = "themeAnimMode";
        rb.checked = key === chosen;
        rb.onchange = () => {
          if (rb.checked) chosen = key;
        };
        lab.appendChild(rb);
        lab.appendChild(
          document.createTextNode(
            key === "frames" ? " Frame Animation" : " Particle System",
          ),
        );
        box.appendChild(lab);
      }

      const row = document.createElement("div");
      row.className = "themeDrawActions";
      const cancel = document.createElement("button");
      cancel.type = "button";
      cancel.className = "btn";
      cancel.textContent = "Cancel";
      const ok = document.createElement("button");
      ok.type = "button";
      ok.className = "btn green";
      ok.textContent = "Continue";
      cancel.onclick = () => {
        modal.remove();
        resolve(null);
      };
      ok.onclick = () => {
        modal.remove();
        resolve(chosen);
      };
      row.appendChild(cancel);
      row.appendChild(ok);
      box.appendChild(row);
      modal.appendChild(box);
      document.body.appendChild(modal);
    });
  }

  function pickAnimLocation(): Promise<"sidebar" | "background" | null> {
    return new Promise((resolve) => {
      const modal = document.createElement("div");
      modal.className = "themeDrawImportModal";
      const box = document.createElement("div");
      box.className = "modeBox themeDrawImportBox";
      const h = document.createElement("h4");
      h.textContent = "Frame animation location";
      box.appendChild(h);
      const hint = document.createElement("p");
      hint.className = "accessHint";
      hint.textContent =
        "Sidebar — draw in the sidebar strip. Background — full-screen animation behind buttons and text.";
      box.appendChild(hint);

      let chosen: "sidebar" | "background" = "background";
      for (const key of ["sidebar", "background"] as const) {
        const lab = document.createElement("label");
        lab.className = "adminThemeCheckLabel";
        const rb = document.createElement("input");
        rb.type = "radio";
        rb.name = "themeAnimLoc";
        rb.checked = key === chosen;
        rb.onchange = () => {
          if (rb.checked) chosen = key;
        };
        lab.appendChild(rb);
        lab.appendChild(
          document.createTextNode(` ${key === "background" ? "Background" : "Sidebar"}`),
        );
        box.appendChild(lab);
      }

      const row = document.createElement("div");
      row.className = "themeDrawActions";
      const cancel = document.createElement("button");
      cancel.type = "button";
      cancel.className = "btn";
      cancel.textContent = "Cancel";
      const ok = document.createElement("button");
      ok.type = "button";
      ok.className = "btn green";
      ok.textContent = "Continue";
      cancel.onclick = () => {
        modal.remove();
        resolve(null);
      };
      ok.onclick = () => {
        modal.remove();
        resolve(chosen);
      };
      row.appendChild(cancel);
      row.appendChild(ok);
      box.appendChild(row);
      modal.appendChild(box);
      document.body.appendChild(modal);
    });
  }

  addHere.onclick = () => {
    const screenKey = detectCurrentThemeScreen();
    openThemeDrawOverlay({
      mode: "static",
      title: `Add Here — ${screenKey}`,
      surface: "canvas",
      onCancel: () => {},
      onConfirm: (result) => {
        placeDecorationInteractive(
          "static",
          result.imageData,
          undefined,
          [screenKey],
          result.opacity,
        );
      },
    });
  };

  addAnim.onclick = () => {
    void (async () => {
      const mode = await pickAnimMode();
      if (!mode) return;
      const colors = opts.getData().colors;

      if (mode === "particles") {
        const setup = await openParticleSetupModal();
        if (!setup) return;
        openThemeDrawOverlay({
          mode: "static",
          title: "Particle art",
          surface: "canvas",
          backgroundColor: colors.backgroundColor,
          onCancel: () => {},
          onConfirm: (result) => {
            const place = particlePlacementFromScreens(setup.screens);
            commitDecoration({
              id: uid("dec"),
              kind: "particles",
              imageData: result.imageData,
              frames: [result.imageData],
              anchor: place.anchor,
              xPct: 0,
              yPct: 0,
              widthPct: 100,
              heightPct: 100,
              screens: setup.screens,
              zIndex: 0,
              opacity: result.opacity,
              layer: place.layer,
              particleConfig: setup.config,
            });
          },
        });
        return;
      }

      const loc = await pickAnimLocation();
      if (!loc) return;
      openThemeDrawOverlay({
        mode: "animated",
        title: loc === "background" ? "Add Animation — Background" : "Add Animation — Sidebar",
        surface: loc,
        backgroundColor: colors.backgroundColor,
        onCancel: () => {},
        onConfirm: (result) => {
          if (loc === "background") {
            commitDecoration({
              id: uid("dec"),
              kind: "animated",
              imageData: result.imageData,
              frames: result.frames,
              anchor: "viewport",
              xPct: 0,
              yPct: 0,
              widthPct: 100,
              heightPct: 100,
              screens: ["background"],
              zIndex: 0,
              opacity: result.opacity,
              layer: "back",
            });
            return;
          }
          commitDecoration({
            id: uid("dec"),
            kind: "animated",
            imageData: result.imageData,
            frames: result.frames,
            anchor: "sidebar",
            xPct: 0,
            yPct: 0,
            widthPct: 100,
            heightPct: 100,
            screens: ["sidebar"],
            zIndex: 2,
            opacity: result.opacity,
            layer: "front",
          });
        },
      });
    })();
  };

  return {
    destroy: () => {
      setThemeDecorEditMode(false);
      closeDeviceFrame();
      bar.remove();
      document.body.classList.remove("theme-preview-mode");
    },
  };
}
